import { ENV } from '@/constants/env';

export const REFRESH_ENDPOINT =
  '/wello/v2/allows/member-oauth/sns/refresh-token';

/** 만료 임박 토큰을 만료로 취급하는 여유 시간 — SSR 프리페치 중 만료를 방지 */
const EXPIRY_SKEW_MS = 30_000;

/** 리프레시 실패 시 재시도 억제 시간 — 죽은 세션의 SSR 요청마다 리프레시 폭주 방지 */
const FAILURE_CACHE_MS = 30_000;

const CACHE_MAX_SIZE = 500;

const decodeJwtPayload = (accessToken: string): Record<string, unknown> => {
  const [, payload] = accessToken.split('.');
  const base64 = payload
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(payload.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), (character) =>
    character.charCodeAt(0),
  );

  return JSON.parse(new TextDecoder().decode(bytes));
};

/** exp를 읽을 수 없는 토큰은 만료로 취급한다(리프레시가 안전한 폴백). */
export const isAccessTokenExpired = (
  accessToken: string,
  nowMs: number,
): boolean => {
  try {
    const { exp } = decodeJwtPayload(accessToken);

    if (typeof exp !== 'number') {
      return true;
    }

    return exp * 1000 <= nowMs + EXPIRY_SKEW_MS;
  } catch {
    return true;
  }
};

interface CachedServerToken {
  accessToken: string | null;
  expiresAtMs: number;
  /**
   * 네트워크·파싱 등 일시 오류인지 여부. true면 캐시하지 않고 다음 요청에서 재시도한다.
   * (죽은 리프레시 토큰 실패는 false로 두어 FAILURE_CACHE_MS 동안 재시도를 억제한다.)
   */
  isTransientFailure: boolean;
}

const requestServerToken = async (
  refreshToken: string,
  nowMs: number,
): Promise<CachedServerToken> => {
  try {
    const response = await fetch(
      `${ENV.NEXT_PUBLIC_API_DOMAIN}${REFRESH_ENDPOINT}?refreshToken=${encodeURIComponent(refreshToken)}`,
      { cache: 'no-store' },
    );
    const data = await response.json();
    const accessToken: string | null =
      data.return_code === 0 ? (data.context?.accessToken ?? null) : null;

    if (!accessToken) {
      //* 백엔드가 응답했으나 토큰이 없다 = 죽은 세션. 30s 네거티브 캐시로 리프레시 폭주를 막는다.
      return {
        accessToken: null,
        expiresAtMs: nowMs + FAILURE_CACHE_MS,
        isTransientFailure: false,
      };
    }

    const { exp } = decodeJwtPayload(accessToken);
    const expiresAtMs =
      typeof exp === 'number'
        ? exp * 1000 - EXPIRY_SKEW_MS
        : nowMs + FAILURE_CACHE_MS;

    return { accessToken, expiresAtMs, isTransientFailure: false };
  } catch {
    //* 네트워크·파싱 오류는 일시 실패로 본다 — 캐시하지 않고 다음 요청에서 바로 재시도한다.
    return { accessToken: null, expiresAtMs: nowMs, isTransientFailure: true };
  }
};

/**
 * SSR 전용: 만료된 액세스 토큰 대신 리프레시 토큰으로 신선한 액세스 토큰을 얻는다.
 * RSC 렌더 중에는 쿠키를 쓸 수 없으므로 발급받은 토큰 쌍은 저장하지 않고
 * 요청 헤더에만 사용한다(백엔드가 리프레시 토큰 재사용을 허용해 클라이언트 세션은 유지됨).
 * 결과는 리프레시 토큰별로 캐시해 동시 프리페치·연속 SSR 요청이 리프레시를 재호출하지 않게 한다.
 */
export const createServerAccessTokenResolver = () => {
  const cache = new Map<string, Promise<CachedServerToken>>();

  return async (
    refreshToken: string,
    nowMs: number,
  ): Promise<string | null> => {
    const pending = cache.get(refreshToken);

    if (pending) {
      const cached = await pending;

      if (cached.expiresAtMs > nowMs) {
        return cached.accessToken;
      }
    }

    if (cache.size >= CACHE_MAX_SIZE) {
      //* 가장 오래된 항목만 제거한다(Map은 삽입 순서 보존) — 전체 clear는 다른 세션의
      //* 유효 토큰까지 날려 리프레시 폭주를 유발한다.
      const oldestKey = cache.keys().next().value;

      if (oldestKey !== undefined) {
        cache.delete(oldestKey);
      }
    }

    const promise = requestServerToken(refreshToken, nowMs);
    cache.set(refreshToken, promise);
    const result = await promise;

    //* 일시 실패는 캐시에 남기지 않아 다음 요청이 즉시 재시도하도록 한다.
    if (result.isTransientFailure) {
      cache.delete(refreshToken);
    }

    return result.accessToken;
  };
};

export const resolveServerAccessToken = createServerAccessTokenResolver();
